import { AttachmentFile, AttachmentFileType, UploadedFile } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import { lookup } from "mrmime";
import { UploadFile } from "../../../types";
import { logError, resolveErrorMessage } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { filesQueryKey } from "../../files/files.keys";
import { FileUploadCallbacks } from "./types";
import { useFileUploadCallbacksRef } from "./utils";

export function useAttachmentUpload<TFileType extends AttachmentFileType = AttachmentFileType>(
	url: string,
	callbacks: FileUploadCallbacks<TFileType, AttachmentFile<TFileType>[]>,
	invalidateQueryKeys?: QueryKey[],
) {
	const cbRef = useFileUploadCallbacksRef<TFileType, AttachmentFile<TFileType>[]>(callbacks);
	const queryClient = useQueryClient();
	const { post, patch, _delete } = useRequest();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const invalidateFileMeta = (attachments: AttachmentFile<TFileType>[]) => {
		for (const attachment of attachments) {
			queryClient.invalidateQueries({
				queryKey: filesQueryKey(attachment.file.id),
				exact: true,
				refetchType: "active",
			});
		}
	};

	const onMutationSettled = () => {
		if (!invalidateQueryKeys) return;
		for (const queryKey of invalidateQueryKeys) {
			queryClient.invalidateQueries({ queryKey, exact: true, refetchType: "active" });
		}
	};

	const uploadAttachmentMutation = useMutation({
		mutationFn: async (args: {
			fileUploads: { rawFile: File; pendingFile: UploadFile<TFileType> }[];
		}): Promise<AttachmentFile<TFileType>[]> => {
			return await Promise.all(
				args.fileUploads.map(async (fileUpload) => {
					const data = new FormData();
					let fileToUpload = fileUpload.rawFile;
					if (!fileUpload.rawFile.type) {
						const mime = lookup(fileUpload.rawFile.name);
						if (mime) {
							fileToUpload = new File([fileUpload.rawFile], fileUpload.rawFile.name, {
								type: mime,
							});
						}
					}
					data.append("file", fileToUpload);
					if (fileUpload.pendingFile.types && fileUpload.pendingFile.types?.length > 0) {
						data.append("types", fileUpload.pendingFile.types.join(","));
					}
					return await post<AttachmentFile<TFileType>>(url, data);
				}),
			);
		},
		onSuccess: (data, args) => {
			cbRef.current.onUploadSuccess(data, args);
		},
		onSettled: onMutationSettled,
		onError: (error: Error, args) => {
			cbRef.current.onUploadError(error, args);
			logError(error);
			triggerErrorModal(error, resolveErrorMessage(error));
		},
	});

	const updateAttachmentMutation = useMutation({
		mutationFn: async (args: {
			pendingFile: UploadedFile<TFileType>;
		}): Promise<AttachmentFile<TFileType>[]> => {
			const response = await patch<AttachmentFile<TFileType>>(
				`${url}/${args.pendingFile.id}`,
				args.pendingFile.types,
			);
			return [response];
		},
		onSuccess: (data, args) => {
			invalidateFileMeta(data);
			cbRef.current.onUpdateSuccess?.(data, args);
		},
		onSettled: onMutationSettled,
		onError: (error: Error, args) => {
			logError(error);
			cbRef.current.onUpdateError?.(error, args);
		},
	});

	const deleteAttachmentMutation = useMutation({
		mutationFn: async (_args: { id: string }) => {
			await _delete(url + `/${_args.id}`, undefined, "none");
		},
		onSuccess: (_data, args) => {
			cbRef.current.onDeleteSuccess?.(args);
		},
		onSettled: onMutationSettled,
		onError: (error: Error, args) => {
			logError(error);
			cbRef.current.onDeleteError?.(error, args);
		},
	});

	return {
		uploadFn: uploadAttachmentMutation.mutate,
		updateFn: updateAttachmentMutation.mutate,
		deleteFn: deleteAttachmentMutation.mutate,
		fileLoading:
			uploadAttachmentMutation.isPending ||
			updateAttachmentMutation.isPending ||
			deleteAttachmentMutation.isPending,
	};
}
